import React, { useState, useRef } from 'react';

import {
  Row,
  Button,
  Form,
  Divider,
  InputNumber,
  Select,
  notification,
  Checkbox,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { WrapperSelect } from 'common/components';

import { useFormatMessage } from 'hooks';

import {
  IMAGE_FORMAT_OPTIONS,
  IMAGE_SIZE_OPTIONS,
  RESOLUTION_OPTIONS,
} from 'static/Constants';

import Messages from 'i18n/messages/folder';

const { Option } = Select;

const generateDigitalAssetData = () => [
  {
    name: 'digitalAssetImageFormat',
    option: IMAGE_FORMAT_OPTIONS,
    transformOption: (item) => ({
      value: item?.toUpperCase(),
      label: item?.toUpperCase(),
    }),
  },
  {
    name: 'digitalAssetImageSize',
    option: IMAGE_SIZE_OPTIONS,
    transformOption: (item) => ({
      value: item,
      label: item,
    }),
    customValueInput: {
      min: 10,
      max: 2500,
    },
  },
  {
    name: 'digitalAssetResolution',
    option: RESOLUTION_OPTIONS,
    transformOption: (item) => ({
      value: item,
      label: !item ? 'Original' : item,
    }),
    customValueInput: {
      min: 75,
      max: 1200,
    },
  },
];

const formLayout = { labelCol: { span: 5 }, wrapperCol: { span: 10 } };

const DownloadFolderAssetSetting = () => {
  const customValueInputRef = useRef({});
  const messages = useFormatMessage(Messages);
  const form = Form.useFormInstance();

  const [assetImageSizeOptions, setAssetImageSizeOptions] =
    useState(IMAGE_SIZE_OPTIONS);
  const [assetResolutionOptions, setAssetResolutionOptions] =
    useState(RESOLUTION_OPTIONS);

  const digitalAssetData = generateDigitalAssetData();

  const getOptions = (type, data) => {
    switch (type) {
      case 'digitalAssetImageSize':
        return assetImageSizeOptions;
      case 'digitalAssetResolution':
        return assetResolutionOptions;
      default:
        return data;
    }
  };

  const openNotification = (message, description) => {
    notification.error({
      message: message,
      description: description,
      duration: 4.5,
    });
  };

  const addAssetImageSizeItem = () => {
    const customAssetImageSize =
      customValueInputRef?.current?.digitalAssetImageSize?.value;

    const formattedValue = `${customAssetImageSize}x${customAssetImageSize}`;

    if (!customAssetImageSize)
      return openNotification(
        messages.imageSizeCustomize,
        messages.imageSizeMissing
      );

    if (assetImageSizeOptions.includes(customAssetImageSize))
      return openNotification(
        messages.imageSizeCustomize,
        messages.imageSizeDuplicated
      );

    setAssetImageSizeOptions([...assetImageSizeOptions, formattedValue]);
  };

  const addAssetResolutionItem = () => {
    const customAssetResolution =
      customValueInputRef?.current?.digitalAssetResolution?.value;

    if (!customAssetResolution)
      return openNotification(
        messages.resolutionCustomize,
        messages.resolutionMissing
      );

    if (assetResolutionOptions.includes(customAssetResolution))
      return openNotification(
        messages.resolutionCustomize,
        messages.resolutionDuplicated
      );

    setAssetResolutionOptions([
      ...assetResolutionOptions,
      customAssetResolution,
    ]);
  };

  const onClickAddCustomValue = (fieldName) => () => {
    switch (fieldName) {
      case 'digitalAssetImageSize':
        addAssetImageSizeItem();
        break;
      case 'digitalAssetResolution':
        addAssetResolutionItem();
        break;
      default:
        return null;
    }
  };

  const onChangeSetting = (fieldName) => (value) => {
    form.setFieldsValue({ [fieldName]: value });
  };

  const selectConfigRender = (item, data) => {
    return (
      <>
        {data}
        {item?.customValueInput ? (
          <>
            <Divider style={{ margin: '4px 0' }} />
            <Row
              align='middle'
              wrap={false}
              style={{ padding: '0px 8px', gap: 8 }}
            >
              <InputNumber
                ref={(el) => (customValueInputRef.current[item.name] = el)}
                style={{ width: '100%', marginBottom: 5 }}
                min={item?.customValueInput?.min}
                max={item?.customValueInput?.max}
              />
              <Button
                type='link'
                icon={<PlusOutlined />}
                style={{ width: 30 }}
                onClick={onClickAddCustomValue(item?.name)}
              ></Button>
            </Row>
          </>
        ) : null}
      </>
    );
  };

  return (
    <>
      {digitalAssetData.map((item) => {
        const options = getOptions(item.name, item.option);
        const transformedOptions = item?.transformOption
          ? options?.map(item?.transformOption)
          : options;

        return (
          <Form.Item
            {...formLayout}
            name={item.name}
            label={messages[item.name]}
            key={item.name}
          >
            <WrapperSelect
              listHeight={110}
              dropdownRender={(data) => selectConfigRender(item, data)}
              getPopupContainer={(trigger) => trigger.parentElement}
              onChange={onChangeSetting(item.name)}
            >
              {transformedOptions.map((option, index) => {
                return (
                  <Option key={index} value={option.value}>
                    {option.label}
                  </Option>
                );
              })}
            </WrapperSelect>
          </Form.Item>
        );
      })}
      <Form.Item
        {...formLayout}
        name='digitalAssetSquare'
        label={'Square Image'}
        valuePropName='checked'
      >
        <Checkbox />
      </Form.Item>
    </>
  );
};

export default DownloadFolderAssetSetting;
